import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  Campaign as CampaignIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  NotificationsActive as ReminderIcon,
  Send as BlastIcon,
} from '@mui/icons-material';
import theme from '../../../../theme/theme';

const LeadNurturingCampaignSelection = () => {
  const navigate = useNavigate();

  const handleJourneyCampaign = () => {
    navigate('/crm/lead-nurturing/create/journey');
  };

  const handleDripCampaign = () => {
    navigate('/crm/lead-nurturing/create/drip');
  };

  const handleReminderCampaign = () => {
    navigate('/crm/lead-nurturing/create/reminder');
  };

  const handleBlastCampaign = () => {
    navigate('/crm/lead-nurturing/create/blast');
  };

  const journeyFeatures = [
    'Multi-step automation',
    'Conditional branching',
    'Event-based triggers',
    'Lead tracking',
    'Complex journey mapping',
  ];

  const dripFeatures = [
    'Sequential messaging',
    'Customizable delays',
    'Multi-channel support',
    'Lead engagement tracking',
    'Automated follow-ups',
  ];

  const reminderFeatures = [
    'Event-based reminders',
    'Relative scheduling',
    'Business hours support',
    'Multiple reminder times',
    'Customizable messages',
  ];

  const blastFeatures = [
    'One-time message blast',
    'Immediate delivery',
    'Multi-channel support',
    'Bulk recipient handling',
    'Delivery tracking',
  ];

  return (
    <Box sx={{ padding: 3, maxWidth: 1200, margin: '0 auto' }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/crm/lead-nurturing')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1" sx={{ color: theme.palette.text.primary }}>
          Create Lead Nurturing Campaign
        </Typography>
      </Box>

      {/* Campaign Type Selection Cards */}
      <Grid container spacing={4}>
        {/* Journey-Based Campaign Card */}
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8],
              },
            }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimelineIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mr: 2 }} />
                <Typography variant="h5" component="h2">
                  Journey-Based Campaign
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" paragraph>
                Create a multi-step journey with conditional paths and automated triggers
              </Typography>
              <List>
                {journeyFeatures.map((feature, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleJourneyCampaign}
                startIcon={<TimelineIcon />}
              >
                Create Journey Campaign
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Drip Campaign Card */}
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8],
              },
            }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CampaignIcon sx={{ fontSize: 40, color: theme.palette.secondary.main, mr: 2 }} />
                <Typography variant="h5" component="h2">
                  Drip Campaign
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" paragraph>
                Send a series of automated messages with customizable delays
              </Typography>
              <List>
                {dripFeatures.map((feature, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon color="secondary" />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                size="large"
                onClick={handleDripCampaign}
                startIcon={<CampaignIcon />}
              >
                Create Drip Campaign
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Reminder Campaign Card */}
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8],
              },
            }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ReminderIcon sx={{ fontSize: 40, color: theme.palette.info.main, mr: 2 }} />
                <Typography variant="h5" component="h2">
                  Reminder Campaign
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" paragraph>
                Set up automated reminders for important events and follow-ups
              </Typography>
              <List>
                {reminderFeatures.map((feature, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon color="info" />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                fullWidth
                variant="contained"
                color="info"
                size="large"
                onClick={handleReminderCampaign}
                startIcon={<ReminderIcon />}
              >
                Create Reminder Campaign
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Blast Campaign Card */}
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8],
              },
            }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BlastIcon sx={{ fontSize: 40, color: theme.palette.warning.main, mr: 2 }} />
                <Typography variant="h5" component="h2">
                  Blast Campaign
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" paragraph>
                Send a one-time message to multiple leads simultaneously
              </Typography>
              <List>
                {blastFeatures.map((feature, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon color="warning" />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                fullWidth
                variant="contained"
                color="warning"
                size="large"
                onClick={handleBlastCampaign}
                startIcon={<BlastIcon />}
              >
                Create Blast Campaign
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LeadNurturingCampaignSelection; 